import { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2 } from 'lucide-react';
import api from '@/lib/api';

// In-memory session cache (shared across component instances).
// Keyed by normalized query. Avoids re-hitting backend for repeat searches.
const SESSION_CACHE = new Map();
const MAX_CACHE_ENTRIES = 200;

function cacheGet(key) {
  return SESSION_CACHE.get(key);
}
function cacheSet(key, value) {
  if (SESSION_CACHE.size >= MAX_CACHE_ENTRIES) {
    const firstKey = SESSION_CACHE.keys().next().value;
    SESSION_CACHE.delete(firstKey);
  }
  SESSION_CACHE.set(key, value);
}

// Build a display line from the address components so a result is never
// shown without its city — Nominatim's own `display_name` sometimes omits
// the city for unincorporated areas even after we've enriched addr.city
// from the ZIP (Alian: "8495 Sweden Boulevard... no muestra Punta Gorda").
function formatResultLine(item) {
  const addr = item.address || {};
  const city = addr.city || addr.town || addr.village || addr.hamlet || addr.locality || '';
  const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
  const parts = [street, city, addr.state, addr.postcode].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : (item.display_name || '');
}

export default function AddressAutocomplete({ value, onChange, placeholder = "Search address...", 'data-testid': dataTestId = 'address-autocomplete' }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [noResultsMsg, setNoResultsMsg] = useState('');
  const [canBypass, setCanBypass] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) { setOpen(false); setNoResultsMsg(''); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = async (q, bypassFilter = false) => {
    const trimmed = (q || '').trim();
    if (trimmed.length < 3) { setResults([]); setLoading(false); setNoResultsMsg(''); return; }

    // 1. Client-side cache first (instant) — only for the normal (non-bypass) search
    const cacheKey = trimmed.toLowerCase() + (bypassFilter ? '__all' : '');
    if (!bypassFilter) {
      const cached = cacheGet(cacheKey);
      if (cached) {
        setResults(cached);
        setOpen(cached.length > 0);
        setNoResultsMsg('');
        setLoading(false);
        return;
      }
    }

    // 2. Track request generation to avoid stale responses overwriting newer ones
    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const res = await api.get(`/geocode/autocomplete?q=${encodeURIComponent(trimmed)}${bypassFilter ? '&bypass_filter=true' : ''}`);
      if (reqId !== requestIdRef.current) return; // stale
      const data = res.data?.results || [];
      cacheSet(cacheKey, data);
      setResults(data);
      setOpen(data.length > 0);
      if (data.length === 0) {
        setNoResultsMsg(res.data?.message || 'No addresses found near your service area.');
        setCanBypass(!!res.data?.can_bypass);
      } else {
        setNoResultsMsg('');
      }
    } catch {
      if (reqId !== requestIdRef.current) return;
      setResults([]);
      setOpen(false);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  };

  const handleSearchAllFlorida = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    search(query, true);
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange({ address: val, lat: null, lng: null });
    setHighlightIdx(-1);
    setNoResultsMsg('');

    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Show loader immediately if length qualifies — makes feedback feel instant
    if (val.trim().length >= 3) {
      const cacheKey = val.trim().toLowerCase();
      if (cacheGet(cacheKey)) {
        // Cached → resolve synchronously, no spinner
        search(val);
        return;
      }
      setLoading(true);
    } else {
      setLoading(false);
    }

    debounceRef.current = setTimeout(() => search(val), 250);
  };

  // Enter key support (Alian's video C: "4101 Indian Drive... no acepta
  // Enter"). ArrowUp/Down move the highlight; Enter picks the highlighted
  // (or first) result. If the dropdown hasn't loaded yet, Enter forces an
  // immediate search instead of waiting for the debounce.
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (results.length > 0) {
        setOpen(true);
        setHighlightIdx((i) => (i + 1) % results.length);
      }
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (results.length > 0) {
        setOpen(true);
        setHighlightIdx((i) => (i - 1 + results.length) % results.length);
      }
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && results.length > 0) {
        const idx = highlightIdx >= 0 ? highlightIdx : 0;
        handleSelect(results[idx]);
      } else if (query.trim().length >= 3) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        search(query);
      }
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleSelect = async (item) => {
    const addr = item.address || {};
    // Bug fix (Alian's video 0:21-0:51): Nominatim sometimes returns a different
    // house_number than what the user typed. Preserve the user-typed number
    // when it's present and differs from the result.
    const typedNumberMatch = (query || '').trim().match(/^(\d+[A-Za-z]?)\s+/);
    const typedHouseNumber = typedNumberMatch ? typedNumberMatch[1] : '';
    const effectiveHouseNumber = typedHouseNumber || addr.house_number || '';
    const streetParts = [effectiveHouseNumber, addr.road].filter(Boolean);
    const streetAddress = streetParts.length > 0 
      ? streetParts.join(' ')
      : item.display_name?.split(',').slice(0, 2).join(',').trim() || '';

    let cityVal =
      addr.city ||
      addr.town ||
      addr.village ||
      addr.hamlet ||
      addr.municipality ||
      addr.city_district ||
      addr.suburb ||
      addr.locality ||
      '';

    let countyVal = (addr.county || '').replace(' County', '');
    const zipVal = addr.postcode || '';

    // ZIP → County fallback when Nominatim omits the county
    if (!countyVal && zipVal) {
      try {
        const res = await api.get(`/geocode/county-by-zip?zip_code=${zipVal}`);
        if (res.data?.success && res.data.county) {
          countyVal = res.data.county;
        }
      } catch { /* silent */ }
    }

    // ZIP → USPS City fallback for unincorporated areas (e.g. 33875 → Sebring)
    if (!cityVal && zipVal) {
      try {
        const res = await api.get(`/geocode/city-by-zip?zip_code=${zipVal}`);
        if (res.data?.success && res.data.city) {
          cityVal = res.data.city;
        }
      } catch { /* silent */ }
    }

    const result = {
      address: streetAddress,
      city: cityVal,
      county: countyVal,
      state: addr.state || '',
      zip_code: zipVal,
      lat: item.lat,
      lng: item.lon
    };
    setQuery(result.address);
    setOpen(false);
    setNoResultsMsg('');
    onChange(result);
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={dataTestId}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          data-testid={`${dataTestId}-input`}
          value={query}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="pl-10 pr-8"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />}
      </div>
      {!loading && !open && noResultsMsg && query.trim().length >= 3 && (
        <div className="absolute z-50 w-full mt-1 bg-amber-50 rounded-lg shadow-md border border-amber-200 p-3" data-testid={`${dataTestId}-no-results`}>
          <p className="text-sm text-amber-800">{noResultsMsg}</p>
          {canBypass && (
            <button
              type="button"
              data-testid={`${dataTestId}-search-all-florida`}
              onClick={handleSearchAllFlorida}
              className="mt-2 text-sm font-medium text-amber-700 hover:text-amber-900 hover:underline"
            >
              Search all of Florida
            </button>
          )}
        </div>
      )}
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
          {results.map((item, i) => (
            <button
              key={i}
              data-testid={`${dataTestId}-option-${i}`}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setHighlightIdx(i)}
              className={`w-full text-left px-3 py-2.5 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0 ${i === highlightIdx ? 'bg-blue-50' : ''}`}
            >
              <p className="text-sm text-gray-800 truncate">{formatResultLine(item)}</p>
              <p className={`text-[10px] mt-0.5 flex items-center gap-1 ${item.distance_miles != null && item.distance_miles > 150 ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                {item.address?.county && `${item.address.county} County`}
                {item.address?.county && item.distance_miles != null && ' · '}
                {item.distance_miles != null && `${item.distance_miles} mi from office`}
                {item.distance_miles != null && item.distance_miles > 150 && ' (far)'}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
